import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Loader2, Award, Printer, Lock, Share2 } from "lucide-react";

export default function Certificate() {
  const navigate = useNavigate();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/me/certificate").then((r) => setCert(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]"><Navbar /><div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-slate-900" /></div></div>
    );
  }

  const issued = cert?.issued_at ? new Date(cert.issued_at) : new Date();
  const dateStr = issued.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="print:hidden"><Navbar /></div>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {!cert?.eligible ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center" data-testid="certificate-locked">
            <Lock className="w-10 h-10 text-slate-300 mx-auto mb-4" />
            <h1 className="font-display text-2xl font-bold text-slate-900">Certificate locked</h1>
            <p className="text-slate-600 mt-2">
              Complete all {cert?.total_modules || 15} modules to unlock your certificate. You've completed {cert?.modules_completed || 0} so far.
            </p>
            <Button className="mt-6 bg-slate-900 hover:bg-slate-800" onClick={() => navigate("/dashboard")}>Back to dashboard</Button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 mb-5 print:hidden flex-wrap">
              <h1 className="font-display text-2xl font-bold text-slate-900">Your certificate</h1>
              <div className="flex gap-2 flex-wrap">
                <a
                  href={`https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${encodeURIComponent(cert.course_title)}&organizationName=${encodeURIComponent("High-Ticket Blueprint")}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Button variant="outline" data-testid="certificate-linkedin-button">
                    <Share2 className="w-4 h-4 mr-2" /> Add to LinkedIn
                  </Button>
                </a>
                <Button onClick={() => window.print()} className="bg-slate-900 hover:bg-slate-800" data-testid="certificate-print-button">
                  <Printer className="w-4 h-4 mr-2" /> Print / Save as PDF
                </Button>
              </div>
            </div>
            <div className="bg-white border-[6px] border-slate-900 rounded-sm p-10 sm:p-16 text-center shadow-xl" data-testid="certificate-document">
              <div className="border border-amber-400 rounded-sm p-8 sm:p-12">
                <Award className="w-14 h-14 text-amber-600 mx-auto" />
                <p className="mt-6 text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">Certificate of Completion</p>
                <p className="mt-8 text-sm text-slate-500">This certifies that</p>
                <p className="mt-2 font-display text-3xl sm:text-4xl font-extrabold text-slate-900" data-testid="certificate-name">{cert.name || "Student"}</p>
                <p className="mt-6 text-sm text-slate-500">has successfully completed all {cert.total_modules} modules of</p>
                <p className="mt-2 font-display text-xl sm:text-2xl font-bold text-slate-900">{cert.course_title}</p>
                <div className="mt-10 flex items-center justify-center gap-10">
                  <div>
                    <p className="font-display text-sm font-semibold text-slate-900 border-t border-slate-300 pt-2 px-6">{dateStr}</p>
                    <p className="text-xs text-slate-400 mt-1">Date</p>
                  </div>
                  <div>
                    <p className="font-display text-sm font-semibold text-slate-900 border-t border-slate-300 pt-2 px-6">High-Ticket Blueprint</p>
                    <p className="text-xs text-slate-400 mt-1">Issued by</p>
                  </div>
                </div>
                <p className="mt-10 text-[10px] text-slate-400 leading-relaxed">
                  Educational completion only. This certificate recognises course completion and does not guarantee income or business results.
                </p>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
